import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    memo,
    lazy,
    Suspense,
    Component,
} from "react";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import SideNavigation from "./SideNavigation";
import TopBar from "./TopBar";
import { logoutUser, clearJustLoggedIn } from "../store";

// === Lazy-load pages ===
const Dashboard = lazy(() => import("../pages/Dashboard"));
const Donations = lazy(() => import("../pages/Donations"));
const DonationDetails = lazy(() => import("../pages/DonationDetails"));
const Donors = lazy(() => import("../pages/Donors"));
const ContactProfile = lazy(() => import("../pages/ContactProfile"));
const Clubs = lazy(() => import("../pages/Clubs"));
const ClubDetails = lazy(() => import("../pages/ClubDetails"));
const CreateNewClub = lazy(() => import("../pages/CreateNewClub"));
const EditClub = lazy(() => import("../pages/EditClub"));
const Campaigns = lazy(() => import("../pages/Campaigns"));
const CreateCampaign = lazy(() => import("../pages/CreateCampaign"));
const EditCampaign = lazy(() => import("../pages/EditCampaign"));
const CampaignDetails = lazy(() => import("../pages/CampaignDetails"));
const Funds = lazy(() => import("../pages/Funds"));
const CreateFund = lazy(() => import("../pages/CreateFund"));
const EditFund = lazy(() => import("../pages/EditFund"));
const FundDetails = lazy(() => import("../pages/FundDetails"));
const CrowdfundPage = lazy(() => import("../pages/Crowdfunding"));
const CrowdfundDetails = lazy(() => import("../pages/CrowdfundDetails"));
const EditCrowdfund = lazy(() => import("../pages/EditCrowdfund"));
const CreateCrowdfund = lazy(() => import("../pages/CreateCrowdfund"));
const Appeals = lazy(() => import("../pages/Appeals"));
const CreateAppeal = lazy(() => import("../pages/CreateAppeal"));
const EditAppeal = lazy(() => import("../pages/EditAppeal"));
const AppealDetails = lazy(() => import("../pages/AppealDetails"));
const Events = lazy(() => import("../pages/Events"));
const CreateEvent = lazy(() => import("../pages/CreateEvent"));
const EditEvent = lazy(() => import("../pages/EditEvent"));
const EventDetail = lazy(() => import("../pages/EventDetail"));
const Volunteers = lazy(() => import("../pages/Volunteers"));
const RefundRequests = lazy(() => import("../pages/RefundRequests"));
const TransactionHistory = lazy(() => import("../pages/TransactionHistory"));
const BulkEmails = lazy(() => import("../pages/BulkEmails"));
const CustomQuery = lazy(() => import("../pages/CustomQuery"));
const Pages = lazy(() => import("../pages/Pages"));
const PageEditor = lazy(() => import("../pages/PageEditor"));
const NavbarCMS = lazy(() => import("../pages/NavbarCMS"));
const FooterCMS = lazy(() => import("../pages/FooterCMS"));
const DataEntryAndImport = lazy(() => import("../pages/DataEntryAndImport"));
const Prospects = lazy(() => import("../pages/Prospects"));
const CreateProspect = lazy(() => import("../pages/CreateProspect"));
const ProspectDetails = lazy(() => import("../pages/ProspectDetails"));
const EditProspect = lazy(() => import("../pages/EditProspect"));
const NewsletterSubscribers = lazy(() => import("../pages/NewsletterSubscribers"));
const Reports = lazy(() => import("../pages/Reports"));
const EOFYStatements = lazy(() => import("../pages/EOFYStatements"));
const CrmUsers = lazy(() => import("../pages/CrmUsers"));
const ReceiptSettings = lazy(() => import("../pages/ReceiptSettings"));

// === Loading spinner ===
const PageLoader: React.FC = memo(() => (
    <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
));

// === Constants ===
const NAVIGATION_LABELS = ["dashboard", "donations", "contact-management", "circles", "giving-history", "campaigns", "funds", "appeals", "crowdfunding", "analytics", "event-management", "volunteers", "refund-request", "navigation", "footer", "transaction-history", "bulk-emails", "custom-query", "pages", "data-entry-and-import", "prospects", "newsletter-subscribers", "reports", "eof-statements", "crm-users", "receipt-settings"] as const;
const DEFAULT_PAGE = "dashboard" as const;

// === Types ===
export type Page =
    | "dashboard"
    | "donations"
    | "donation-details"
    | "contact-management"
    | "contact-profile"
    | "circles"
    | "circle-details"
    | "create-new-club"
    | "edit-club"
    | "giving-history"
    | "campaigns"
    | "create-campaign"
    | "edit-campaign"
    | "campaign-details"
    | "funds"
    | "create-fund"
    | "edit-fund"
    | "fund-details"
    | "crowdfunding"
    | "crowdfund-details"
    | "edit-crowdfund"
    | "create-crowdfund"
    | "appeals"
    | "create-appeal"
    | "edit-appeal"
    | "appeal-details"
    | "analytics"
    | "event-management"
    | "create-event"
    | "edit-event"
    | "event-details"
    | "volunteers"
    | "refund-request"
    | "navigation"
    | "footer"
    | "transaction-history"
    | "bulk-emails"
    | "custom-query"
    | "pages"
    | "edit-page"
    | "data-entry-and-import"
    | "prospects"
    | "create-prospect"
    | "edit-prospect"
    | "prospect-details"
    | "newsletter-subscribers"
    | "eof-statements"
    | "reports"
    | "receipt-settings"
    | "crm-users";

export type NavigationLabel = typeof NAVIGATION_LABELS[number];

interface RouteConfig {
    path: string;
    page: Page;
    requiresId?: boolean;
    parentPage?: Page;
}

// === Route Config ===
const ROUTE_CONFIG: Record<Page, RouteConfig> = {
    dashboard: { path: "/", page: "dashboard" },
    donations: { path: "/donations", page: "donations" },
    "donation-details": {
        path: "/donation-details/",
        page: "donation-details",
        requiresId: true,
        parentPage: "donations",
    },
    "contact-management": { path: "/contact-management", page: "contact-management" },
    "contact-profile": {
        path: "/contact-profile/",
        page: "contact-profile",
        requiresId: true,
        parentPage: "contact-management",
    },
    circles: { path: "/circles", page: "circles" },
    "circle-details": {
        path: "/circle-details/",
        page: "circle-details",
        requiresId: true,
        parentPage: "circles",
    },
    "create-new-club": {
        path: "/create-new-circle",
        page: "create-new-club",
        parentPage: "circles",
    },
    "edit-club": {
        path: "/edit-circle/",
        page: "edit-club",
        requiresId: true,
        parentPage: "circles",
    },
    "giving-history": { path: "/giving-history", page: "giving-history" },
    campaigns: { path: "/campaigns", page: "campaigns" },
    "create-campaign": {
        path: "/create-campaign",
        page: "create-campaign",
        parentPage: "campaigns",
    },
    "edit-campaign": {
        path: "/edit-campaign/",
        page: "edit-campaign",
        requiresId: true,
        parentPage: "campaigns",
    },
    "campaign-details": {
        path: "/campaign-details/",
        page: "campaign-details",
        requiresId: true,
        parentPage: "campaigns",
    },
    funds: { path: "/funds", page: "funds" },
    "fund-details": {
        path: "/fund-details/",
        page: "fund-details",
        requiresId: true,
        parentPage: "funds",
    },
    "create-fund": {
        path: "/create-fund",
        page: "create-fund",
        parentPage: "funds",
    },
    "edit-fund": {
        path: "/edit-fund/",
        page: "edit-fund",
        requiresId: true,
        parentPage: "funds",
    },
    appeals: { path: "/appeals", page: "appeals" },
    "create-appeal": {
        path: "/create-appeal",
        page: "create-appeal",
        parentPage: "appeals",
    },
    "edit-appeal": {
        path: "/edit-appeal/",
        page: "edit-appeal",
        requiresId: true,
        parentPage: "appeals",
    },
    "appeal-details": {
        path: "/appeal-details/",
        page: "appeal-details",
        requiresId: true,
        parentPage: "appeals",
    },
    analytics: { path: "/analytics", page: "analytics" },
    "event-management": { path: "/event-management", page: "event-management" },
    "create-event": {
        path: "/create-event",
        page: "create-event",
        parentPage: "event-management",
    },
    "edit-event": {
        path: "/edit-event/",
        page: "edit-event",
        requiresId: true,
        parentPage: "event-management",
    },
    "event-details": {
        path: "/event-details/",
        page: "event-details",
        requiresId: true,
        parentPage: "event-management",
    },
    volunteers: { path: "/volunteers", page: "volunteers" },
    "refund-request": { path: "/refund-request", page: "refund-request" },
    navigation: { path: "/navigation", page: "navigation" },
    footer: { path: "/footer", page: "footer" },
    "transaction-history": { path: "/transaction-history", page: "transaction-history" },
    "bulk-emails": { path: "/bulk-emails", page: "bulk-emails" },
    "custom-query": { path: "/custom-query", page: "custom-query" },
    pages: { path: "/pages", page: "pages" },
    "edit-page": {
        path: "/edit-page/",
        page: "edit-page",
        requiresId: true,
        parentPage: "pages",
    },
    "data-entry-and-import": { path: "/data-entry-and-import", page: "data-entry-and-import" },
    prospects: { path: "/prospects", page: "prospects" },
    "create-prospect": {
        path: "/create-prospect",
        page: "create-prospect",
        parentPage: "prospects",
    },
    "edit-prospect": {
        path: "/edit-prospect/",
        page: "edit-prospect",
        requiresId: true,
        parentPage: "prospects",
    },
    "prospect-details": {
        path: "/prospect-details/",
        page: "prospect-details",
        requiresId: true,
        parentPage: "prospects",
    },
    crowdfunding: { path: "/crowdfunding", page: "crowdfunding" },
    "crowdfund-details": {
        path: "/crowdfunding-details/",
        page: "crowdfund-details",
        requiresId: true,
        parentPage: "crowdfunding",
    },
    "edit-crowdfund": {
        path: "/edit-crowdfunding/",
        page: "edit-crowdfund",
        requiresId: true,
        parentPage: "crowdfunding",
    },
    "create-crowdfund": {
        path: "/create-crowdfunding",
        page: "create-crowdfund",
        parentPage: "crowdfunding",
    },
    "newsletter-subscribers": { path: "/newsletter-subscribers", page: "newsletter-subscribers" },
    "eof-statements": { path: "/eof-statements", page: "eof-statements" },
    reports: { path: "/reports", page: "reports" },
    "crm-users": { path: "/crm-users", page: "crm-users" },
    "receipt-settings": { path: "/receipt-settings", page: "receipt-settings", parentPage: "settings" as Page },
};

// === Navigation Maps ===
const NAVIGATION_TO_PAGE_MAP: Record<NavigationLabel, Page> = {
    dashboard: "dashboard",
    donations: "donations",
    "contact-management": "contact-management",
    circles: "circles",
    "giving-history": "giving-history",
    campaigns: "campaigns",
    funds: "funds",
    appeals: "appeals",
    analytics: "analytics",
    "event-management": "event-management",
    volunteers: "volunteers",
    "refund-request": "refund-request",
    navigation: "navigation",
    footer: "footer",
    "transaction-history": "transaction-history",
    "bulk-emails": "bulk-emails",
    "custom-query": "custom-query",
    pages: "pages",
    "data-entry-and-import": "data-entry-and-import",
    prospects: "prospects",
    crowdfunding: "crowdfunding",
    "newsletter-subscribers": "newsletter-subscribers",
    "eof-statements": "eof-statements",
    reports: "reports",
    "receipt-settings": "navigation",
    "crm-users": "crm-users",
};

const PAGE_TO_NAVIGATION_MAP: Record<Page, NavigationLabel> = {
    dashboard: "dashboard",
    donations: "donations",
    "donation-details": "donations",
    "contact-management": "contact-management",
    "contact-profile": "contact-management",
    circles: "circles",
    "circle-details": "circles",
    "create-new-club": "circles",
    "edit-club": "circles",
    "giving-history": "giving-history",
    campaigns: "campaigns",
    "create-campaign": "campaigns",
    "edit-campaign": "campaigns",
    "campaign-details": "campaigns",
    funds: "funds",
    "create-fund": "funds",
    "edit-fund": "funds",
    "fund-details": "funds",
    appeals: "appeals",
    "create-appeal": "appeals",
    "edit-appeal": "appeals",
    "appeal-details": "appeals",
    crowdfunding: "crowdfunding",
    "crowdfund-details": "crowdfunding",
    "edit-crowdfund": "crowdfunding",
    "create-crowdfund": "crowdfunding",
    analytics: "analytics",
    "event-management": "event-management",
    "create-event": "event-management",
    "edit-event": "event-management",
    "event-details": "event-management",
    volunteers: "volunteers",
    "refund-request": "refund-request",
    navigation: "navigation",
    footer: "footer",
    "transaction-history": "transaction-history",
    "bulk-emails": "bulk-emails",
    "custom-query": "custom-query",
    pages: "pages",
    "edit-page": "pages",
    "data-entry-and-import": "data-entry-and-import",
    prospects: "prospects",
    "create-prospect": "prospects",
    "edit-prospect": "prospects",
    "prospect-details": "prospects",
    "newsletter-subscribers": "newsletter-subscribers",
    "eof-statements": "eof-statements",
    reports: "reports",
    "receipt-settings": "navigation",
    "crm-users": "crm-users",
};

// === Utility Functions ===
const isNavigationLabel = (value: string): value is NavigationLabel =>
    NAVIGATION_LABELS.includes(value.toLowerCase() as NavigationLabel);

const normalizeNavigation = (nav: string[]): NavigationLabel[] => {
    const normalized = nav
        .map((n) => n.toLowerCase().trim().replace(/\s+/g, '-'))
        .filter(isNavigationLabel);
    return Array.from(new Set(normalized));
};

const getPageFromPath = (path: string): Page => {
    const normalizedPath = path.toLowerCase();
    for (const config of Object.values(ROUTE_CONFIG)) {
        const normalizedConfigPath = config.path.toLowerCase();
        if (config.requiresId && normalizedPath.startsWith(normalizedConfigPath)) return config.page;
        if (normalizedPath === normalizedConfigPath) return config.page;
    }
    return DEFAULT_PAGE;
};

const getPageIdFromPath = (path: string): string | null => {
    const normalizedPath = path.toLowerCase();
    for (const [page, config] of Object.entries(ROUTE_CONFIG)) {
        const normalizedConfigPath = config.path.toLowerCase();
        if (config.requiresId && normalizedPath.startsWith(normalizedConfigPath)) {
            let id = path.substring(config.path.length);
            // Remove leading slash if present
            if (id.startsWith('/')) {
                id = id.substring(1);
            }
            id = id.trim();
            return id && id.length > 0 ? id : null;
        }
    }
    return null;
};

const getPathFromPage = (page: Page, id?: string): string => {
    const config = ROUTE_CONFIG[page];
    const path = config.requiresId && id ? `${config.path}${id}` : config.path;
    console.log('getPathFromPage:', { page, id, configPath: config.path, resultPath: path });
    return path;
};

const hasPageAccess = (page: Page, navigation: NavigationLabel[], userRole?: string): boolean => {
    // Special case for reports - only for global role
    if (page === "reports" && userRole === "global") return true;
    if (page === "crm-users" && (userRole === "global" || userRole === "donorCare")) return true;

    // Special case for eof-statements - inherits from donations or eof-statements
    if (page === "eof-statements" && (navigation.includes("donations") || navigation.includes("eof-statements"))) return true;

    const navLabel = PAGE_TO_NAVIGATION_MAP[page];
    if (navigation.includes(navLabel)) return true;

    const parentPage = ROUTE_CONFIG[page].parentPage;
    return parentPage ? navigation.includes(PAGE_TO_NAVIGATION_MAP[parentPage]) : false;
};

// === Custom Hook for Navigation ===
interface UseNavigationReturn {
    currentPage: Page | null;
    setCurrentPage: React.Dispatch<React.SetStateAction<Page | null>>;
    navigation: NavigationLabel[];
    firstAvailablePage: Page;
    isInitialized: boolean;
}

const useNavigation = (rawNavigation: string[], justLoggedIn: boolean, dispatch: any, userRole?: string): UseNavigationReturn => {
    const [currentPage, setCurrentPage] = useState<Page | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);

    // Normalize navigation
    const navigation = useMemo(() => normalizeNavigation(rawNavigation), [rawNavigation]);

    // Get first available page
    const firstAvailablePage = useMemo<Page>(
        () => navigation.map((n) => NAVIGATION_TO_PAGE_MAP[n])[0] ?? DEFAULT_PAGE,
        [navigation]
    );

    // Logout if user has no valid navigation
    useEffect(() => {
        if (isInitialized && navigation.length === 0) {
            console.warn("No valid navigation items — logging out");
            dispatch(logoutUser());
        }
    }, [navigation, isInitialized, dispatch]);

    // Initial mount and navigation handling
    useEffect(() => {
        if (!isInitialized && navigation.length > 0) {
            const currentPath = window.location.pathname;
            const requestedPage = getPageFromPath(currentPath);

            // If user just logged in, always redirect to first available page
            if (justLoggedIn) {
                console.log("User just logged in - redirecting to first available page:", firstAvailablePage);
                setCurrentPage(firstAvailablePage);
                window.history.replaceState({}, "", getPathFromPage(firstAvailablePage));
                dispatch(clearJustLoggedIn());
            } else {
                // Check if user has access to the current URL
                if (hasPageAccess(requestedPage, navigation, userRole)) {
                    setCurrentPage(requestedPage);
                } else {
                    setCurrentPage(firstAvailablePage);
                    window.history.replaceState({}, "", getPathFromPage(firstAvailablePage));
                }
            }
            setIsInitialized(true);
        }
    }, [navigation, firstAvailablePage, isInitialized, justLoggedIn, dispatch]);


    // Handle browser navigation (back/forward buttons and direct URL changes)
    useEffect(() => {
        if (!isInitialized) return;

        const handleNavigationChange = () => {
            const currentPath = window.location.pathname;
            const requestedPage = getPageFromPath(currentPath);

            // Check if user has access to the requested page
            if (hasPageAccess(requestedPage, navigation, userRole)) {
                console.log("Access granted to:", requestedPage);
                setCurrentPage(requestedPage);
            } else {
                console.log("Access denied to:", requestedPage, "redirecting to:", firstAvailablePage);
                setCurrentPage(firstAvailablePage);
                window.history.replaceState({}, "", getPathFromPage(firstAvailablePage));
            }
        };

        // Handle browser back/forward buttons
        window.addEventListener("popstate", handleNavigationChange);

        // Override pushState and replaceState to detect programmatic navigation
        const originalPushState = window.history.pushState;
        const originalReplaceState = window.history.replaceState;

        window.history.pushState = function (...args) {
            originalPushState.apply(this, args);
            setTimeout(handleNavigationChange, 0);
        };

        window.history.replaceState = function (...args) {
            originalReplaceState.apply(this, args);
            setTimeout(handleNavigationChange, 0);
        };

        // Check for URL changes on focus (when user comes back to tab)
        const handleFocus = () => {
            setTimeout(handleNavigationChange, 0);
        };
        window.addEventListener("focus", handleFocus);

        return () => {
            window.removeEventListener("popstate", handleNavigationChange);
            window.removeEventListener("focus", handleFocus);
            window.history.pushState = originalPushState;
            window.history.replaceState = originalReplaceState;
        };
    }, [navigation, firstAvailablePage, isInitialized]);

    // Additional effect to handle URL changes when navigation array updates
    useEffect(() => {
        if (!isInitialized) return;

        const currentPath = window.location.pathname;
        const requestedPage = getPageFromPath(currentPath);

        // Check if user has access to the requested page
        if (hasPageAccess(requestedPage, navigation, userRole)) {
            setCurrentPage(requestedPage);
        } else {
            setCurrentPage(firstAvailablePage);
            window.history.replaceState({}, "", getPathFromPage(firstAvailablePage));
        }
    }, [navigation, isInitialized, firstAvailablePage]);

    // Effect to continuously check URL changes (catches direct navigation)
    useEffect(() => {
        if (!isInitialized || !currentPage) return;

        const checkCurrentUrl = () => {
            const currentPath = window.location.pathname;
            const requestedPage = getPageFromPath(currentPath);

            // Only update if the page is different from current
            if (requestedPage !== currentPage) {
                console.log("URL check - currentPath:", currentPath, "requestedPage:", requestedPage, "currentPage:", currentPage);

                if (hasPageAccess(requestedPage, navigation, userRole)) {
                    console.log("URL check - Access granted, updating to:", requestedPage);
                    setCurrentPage(requestedPage);
                } else {
                    console.log("URL check - Access denied, staying on:", currentPage);
                }
            }
        };

        // Check immediately
        checkCurrentUrl();

        // Check periodically to catch any missed URL changes
        const intervalId = setInterval(checkCurrentUrl, 500);

        return () => clearInterval(intervalId);
    }, [isInitialized, navigation, currentPage]);

    // Expose navigation function for debugging
    React.useEffect(() => {
        (window as any).testNavigation = (url: string) => {
            console.log("Testing navigation to:", url);
            window.history.pushState({}, "", url);
            const requestedPage = getPageFromPath(url);
            console.log("Requested page:", requestedPage);
            console.log("Has access:", hasPageAccess(requestedPage, navigation, userRole));
            if (hasPageAccess(requestedPage, navigation, userRole)) {
                setCurrentPage(requestedPage);
            } else {
                setCurrentPage(firstAvailablePage);
            }
        };
    }, [navigation, firstAvailablePage]);

    return {
        currentPage,
        setCurrentPage,
        navigation,
        firstAvailablePage,
        isInitialized
    };
};

// === Error Boundary ===
interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
    errorInfo?: React.ErrorInfo;
}

class PageErrorBoundary extends Component<
    { children: React.ReactNode; onRetry?: () => void },
    ErrorBoundaryState
> {
    constructor(props: { children: React.ReactNode; onRetry?: () => void }) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        console.error("Page Error:", error, errorInfo);
        this.setState({ error, errorInfo });

        // Log to external service in production
        if (process.env.NODE_ENV === 'production') {
            // TODO: Add error reporting service integration
            console.error('Production error:', { error: error.message, stack: error.stack, errorInfo });
        }
    }

    handleRetry = (): void => {
        this.setState({ hasError: false, error: undefined, errorInfo: undefined });
        this.props.onRetry?.();
    };

    render(): React.ReactNode {
        if (this.state.hasError) {
            return (
                <div className="flex items-center justify-center min-h-[400px] p-4">
                    <div className="text-center max-w-md">
                        <div className="mb-4">
                            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                        </div>
                        <h2 className="text-xl font-semibold text-red-600 mb-2">
                            Something went wrong
                        </h2>
                        <p className="text-gray-600 mb-4">
                            We encountered an unexpected error. Please try refreshing the page or contact support if the problem persists.
                        </p>
                        <div className="space-x-3">
                            <button
                                onClick={this.handleRetry}
                                className="px-4 py-2 bg-blue-600 !text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Try Again
                            </button>
                            <button
                                onClick={() => window.location.reload()}
                                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                            >
                                Refresh Page
                            </button>
                        </div>
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="mt-4 text-left">
                                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                                    Error Details (Development)
                                </summary>
                                <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto">
                                    {this.state.error.stack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

// === Main Component ===
const AppContent: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user, navigation: rawNavigation, justLoggedIn } = useAppSelector((state) => state.auth);

    // Sidebar state
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [isDesktop, setIsDesktop] = useState(false);

    const {
        currentPage,
        setCurrentPage,
        navigation,
        firstAvailablePage,
        isInitialized
    } = useNavigation(rawNavigation, justLoggedIn, dispatch, user?.role);

    // Optimized navigation handler
    const navigateToPage = useCallback(
        (page: Page, id?: string, replace = false) => {
            if (!hasPageAccess(page, navigation, user?.role)) {
                console.warn("Access denied to page:", page, "redirecting to:", firstAvailablePage);
                setCurrentPage(firstAvailablePage);
                const method = replace ? 'replaceState' : 'pushState';
                window.history[method]({}, "", getPathFromPage(firstAvailablePage));
                return false;
            }

            console.log("Navigating to page:", page, "with id:", id);
            setCurrentPage(page);
            const method = replace ? 'replaceState' : 'pushState';
            window.history[method]({}, "", getPathFromPage(page, id));
            return true;
        },
        [navigation, firstAvailablePage, setCurrentPage]
    );

    // Change page via UI
    const handlePageChange = useCallback(
        (page: Page, id?: string) => navigateToPage(page, id),
        [navigateToPage]
    );

    // Detail view navigation
    const handleViewDonation = useCallback(
        (id: number) => navigateToPage("donation-details", String(id)),
        [navigateToPage]
    );

    const handleViewClub = useCallback(
        (id: string) => navigateToPage("circle-details", id),
        [navigateToPage]
    );

    const handleViewDonor = useCallback(
        (id: string) => navigateToPage("contact-profile", id),
        [navigateToPage]
    );

    const handleViewCampaign = useCallback(
        (id: string) => navigateToPage("campaign-details", id),
        [navigateToPage]
    );

    const handleViewFund = useCallback(
        (id: string) => navigateToPage("fund-details", id),
        [navigateToPage]
    );

    const handleViewCrowdfund = useCallback(
        (id: string) => navigateToPage("crowdfund-details", id),
        [navigateToPage]
    );

    const handleViewAppeal = useCallback(
        (id: string) => navigateToPage("appeal-details", id),
        [navigateToPage]
    );

    const handleViewEvent = useCallback(
        (id: string) => navigateToPage("event-details", id),
        [navigateToPage]
    );

    const handleViewVolunteer = useCallback(
        (id: string) => navigateToPage("contact-profile", id),
        [navigateToPage]
    );

    // Get current page ID from URL
    const currentPageId = useMemo(() => {
        if (!currentPage) return null;
        const currentPath = window.location.pathname;
        const id = getPageIdFromPath(currentPath);
        return id;
    }, [currentPage]);

    // Memoized page components to prevent unnecessary re-renders
    const pageComponents = useMemo(() => ({
        dashboard: <Dashboard />,
        donations: <Donations onViewDonation={handleViewDonation} onViewDonor={handleViewDonor} onViewClub={handleViewClub} />,
        "donation-details": currentPageId ? (
            <DonationDetails
                donationId={parseInt(currentPageId)}
                onBack={() => handlePageChange("donations")}
            />
        ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Invalid donation ID</p>
            </div>
        ),
        donors: <Donors onViewDonor={handleViewDonor} />,
        "contact-management": <Donors onViewDonor={handleViewDonor} />,
        "contact-profile": currentPageId ? (
            <ContactProfile
                donorId={currentPageId}
                onBack={() => handlePageChange("contact-management")}
                onViewDonation={handleViewDonation}
                onPageChange={handlePageChange}
            />
        ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Invalid contact ID</p>
            </div>
        ),
        "newsletter-subscribers": <NewsletterSubscribers />,
        circles: (
            <Clubs
                onViewClub={handleViewClub}
                onPageChange={handlePageChange}
            />
        ),
        "circle-details": currentPageId ? (
            <ClubDetails
                clubId={parseInt(currentPageId)}
                onBack={() => handlePageChange("circles")}
                onViewDonation={handleViewDonation}
                onDonorClick={(donorId) => handleViewDonor(String(donorId))}
            />
        ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Invalid club ID</p>
            </div>
        ),
        "create-new-club": <CreateNewClub onBack={() => handlePageChange("circles")} />,
        "edit-club": currentPageId ? (
            <EditClub
                clubId={currentPageId}
                onBack={() => handlePageChange("circles")}
            />
        ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Invalid club ID</p>
            </div>
        ),
        "giving-history": <div className="p-4"><h1 className="text-2xl font-bold">Giving History</h1><p>Coming soon...</p></div>,
        campaigns: <Campaigns onPageChange={handlePageChange} onViewCampaign={handleViewCampaign} />,
        "create-campaign": <CreateCampaign onBack={() => handlePageChange("campaigns")} />,
        "edit-campaign": currentPageId ? (
            <EditCampaign
                campaignId={currentPageId}
                onBack={() => handlePageChange("campaigns")}
            />
        ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Invalid campaign ID</p>
            </div>
        ),
        "campaign-details": currentPageId ? (
            <CampaignDetails
                campaignId={parseInt(currentPageId)}
                onBack={() => handlePageChange("campaigns")}
                onViewFund={handleViewFund}
                onViewAppeal={handleViewAppeal}
                onViewEvent={handleViewEvent}
            />
        ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Invalid campaign ID</p>
            </div>
        ),
        funds: <Funds onPageChange={handlePageChange} onViewFund={handleViewFund} />,
        "create-fund": <CreateFund onBack={() => handlePageChange("funds")} />,
        "edit-fund": currentPageId ? (
            <EditFund
                fundId={currentPageId}
                onBack={() => handlePageChange("funds")}
            />
        ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Invalid fund ID</p>
            </div>
        ),
        "fund-details": currentPageId ? (
            <FundDetails
                fundId={parseInt(currentPageId)}
                onBack={() => handlePageChange("funds")}
            />
        ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Invalid fund ID</p>
            </div>
        ),
        appeals: <Appeals onPageChange={handlePageChange} onViewAppeal={handleViewAppeal} />,
        "create-appeal": <CreateAppeal onBack={() => handlePageChange("appeals")} />,
        "edit-appeal": currentPageId ? (
            <EditAppeal
                appealId={currentPageId}
                onBack={() => handlePageChange("appeals")}
            />
        ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Invalid appeal ID</p>
            </div>
        ),
        "appeal-details": currentPageId ? (
            <AppealDetails
                appealId={parseInt(currentPageId)}
                onBack={() => handlePageChange("appeals")}
                onViewCampaign={handleViewCampaign}
            />
        ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Invalid appeal ID</p>
            </div>
        ),
        analytics: <div className="p-4"><h1 className="text-2xl font-bold">Analytics and reporting</h1><p>Coming soon...</p></div>,
        "event-management": <Events onPageChange={handlePageChange} />,
        "create-event": <CreateEvent onBack={() => handlePageChange("event-management")} />,
        "edit-event": currentPageId ? (
            <EditEvent
                eventId={currentPageId}
                onBack={() => handlePageChange("event-management")}
            />
        ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Invalid event ID</p>
            </div>
        ),
        "event-details": currentPageId ? (
            <EventDetail
                eventId={currentPageId}
                onBack={() => handlePageChange("event-management")}
            />
        ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Invalid event ID</p>
            </div>
        ),
        volunteers: <Volunteers onViewVolunteer={handleViewVolunteer} />,
        "refund-request": <RefundRequests onViewDonation={handleViewDonation} />,
        navigation: <NavbarCMS />,
        footer: <FooterCMS />,
        "transaction-history": <TransactionHistory onViewDonation={handleViewDonation} />,
        "bulk-emails": <BulkEmails />,
        "custom-query": <CustomQuery />,
        pages: <Pages onPageChange={handlePageChange} />,
        "edit-page": currentPageId ? (
            <PageEditor
                pageId={currentPageId}
                onBack={() => handlePageChange("pages")}
            />
        ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Invalid page ID</p>
            </div>
        ),
        "data-entry-and-import": <DataEntryAndImport />,
        prospects: <Prospects onPageChange={handlePageChange} />,
        "create-prospect": <CreateProspect onBack={() => handlePageChange("prospects")} />,
        "edit-prospect": currentPageId ? (
            <EditProspect
                prospectId={currentPageId}
                onBack={() => handlePageChange("prospects")}
            />
        ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Invalid prospect ID</p>
            </div>
        ),
        "prospect-details": currentPageId ? (
            <ProspectDetails
                prospectId={currentPageId}
                onBack={() => handlePageChange("prospects")}
            />
        ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Invalid prospect ID</p>
            </div>
        ),
        crowdfunding: <CrowdfundPage onPageChange={handlePageChange} onViewCrowdfund={handleViewCrowdfund} />,
        "crowdfund-details": currentPageId ? (
            <CrowdfundDetails
                crowdfundId={parseInt(currentPageId)}
                onBack={() => handlePageChange("crowdfunding")}
            />
        ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Invalid crowdfunding ID</p>
            </div>
        ),
        "edit-crowdfund": currentPageId ? (
            <EditCrowdfund
                crowdfundId={currentPageId}
                onBack={() => handlePageChange("crowdfunding")}
            />
        ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Invalid crowdfunding ID</p>
            </div>
        ),
        "create-crowdfund": <CreateCrowdfund onBack={() => handlePageChange("crowdfunding")} />,
        "eof-statements": <EOFYStatements />,
        reports: <Reports />,
        "crm-users": <CrmUsers />,
        "receipt-settings": <ReceiptSettings />,
    }), [handleViewDonation, handleViewClub, handleViewDonor, handleViewCampaign, handleViewVolunteer, handleViewAppeal, handleViewEvent, handleViewFund, handleViewCrowdfund, handlePageChange, currentPageId]);

    // Render current page
    const renderPage = useMemo(() => {
        // Don't render any page until navigation is initialized
        if (!isInitialized || !currentPage) {
            return <PageLoader />;
        }

        return (
            <PageErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                    {pageComponents[currentPage] ?? pageComponents.dashboard}
                </Suspense>
            </PageErrorBoundary>
        );
    }, [currentPage, pageComponents, isInitialized]);

    const headerPage: NavigationLabel = currentPage ? PAGE_TO_NAVIGATION_MAP[currentPage] : 'dashboard';

    // Sidebar handlers
    const handleToggleCollapse = useCallback(() => {
        setIsCollapsed(prev => !prev);
    }, []);

    const handleMobileToggle = useCallback(() => {
        setIsMobileOpen(prev => !prev);
    }, []);

    // Auto-collapse sidebar when on contact-profile page
    const isContactProfilePage = currentPage === "contact-profile" && isInitialized;

    useEffect(() => {
        if (isContactProfilePage) {
            setIsCollapsed(true);
        }
    }, [isContactProfilePage]);

    // Handle responsive behavior
    useEffect(() => {
        const handleResize = () => {
            const isDesktopView = window.innerWidth >= 1280;
            setIsDesktop(isDesktopView);
            if (isDesktopView) {
                setIsMobileOpen(false);
            }
        };

        // Set initial state
        handleResize();

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Auto-close mobile menu when navigating to contact-profile page
    useEffect(() => {
        if (isContactProfilePage && isMobileOpen) {
            setIsMobileOpen(false);
        }
    }, [isContactProfilePage, isMobileOpen]);

    return (
        <div
            className="min-h-screen flex"
            style={{ backgroundColor: "var(--bg-secondary)" }}
            role="main"
        >
            {/* Side Navigation */}
            <SideNavigation
                currentPage={headerPage}
                onPageChange={handlePageChange}
                isCollapsed={isCollapsed}
                onToggleCollapse={handleToggleCollapse}
                isMobileOpen={isMobileOpen}
                onMobileToggle={handleMobileToggle}
            />

            {/* Main Content Area */}
            <div
                className="flex-1 flex flex-col min-w-0 main-content-area"
                style={{
                    marginLeft: isDesktop ? (isCollapsed ? "80px" : "280px") : "0px",
                }}
            >
                {/* Top Bar */}
                <TopBar
                    onMobileMenuToggle={handleMobileToggle}
                    sidebarWidth={isCollapsed ? "80px" : "280px"}
                    isCollapsed={isCollapsed}
                />

                {/* Main Content */}
                <main
                    className="flex-1 p-4 lg:p-6"
                    aria-live="polite"
                    aria-label={`Current page: ${currentPage}`}
                >
                    {renderPage}
                </main>
            </div>
        </div>
    );
};

// Add display name for better debugging
AppContent.displayName = 'AppContent';

export default memo(AppContent);
